import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "product-display-container" }
const _hoisted_2 = { class: "section-header" }
const _hoisted_3 = { class: "section-title" }
const _hoisted_4 = {
  key: 0,
  class: "scroll-controls"
}
const _hoisted_5 = ["onClick"]
const _hoisted_6 = ["onClick"]
const _hoisted_7 = { class: "product-scroll-container" }
const _hoisted_8 = ["onClick"]
const _hoisted_9 = { class: "product-image-container" }
const _hoisted_10 = ["src", "alt"]
const _hoisted_11 = {
  key: 0,
  class: "new-badge"
}
const _hoisted_12 = {
  key: 1,
  class: "hot-badge"
}
const _hoisted_13 = { class: "product-name" }
const _hoisted_14 = { class: "product-description" }
const _hoisted_15 = { class: "product-price" }
const _hoisted_16 = { class: "compatibility-section" }
const _hoisted_17 = { class: "compatibility-title" }
const _hoisted_18 = { class: "car-brands-grid" }
const _hoisted_19 = ["src", "alt"]

import { ref, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

// 主题信息接口
export interface TopicVO {
  id: number;
  name: string;
  nameEn: string;
  sort: number;
}

// 产品项接口
export interface ProductItem {
  id: number;
  imgUrl: string;
  imgUrlen: string;
  sort: number;
  productName: string;
  productNameEn: string;
  productPrice: number;
  productDesc: string;
  productDescEn: string;
  topicVO: TopicVO;
  isNew?: boolean;
  isHot?: boolean;
}

// 产品主题接口
export interface ProductTopic {
  id: number;
  name: string;
  nameEn: string;
  sort: number;
  products: ProductItem[];
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ProductDisplay',
  setup(__props) {

const router = useRouter();
const { locale } = useI18n();

// 获取当前语言
const currentLanguage = ref(locale.value);

// 监听语言变化
watch(
  () => locale.value,
  (newLocale) => {
    currentLanguage.value = newLocale;
    console.log('Language changed to:', newLocale);
  },
  { immediate: true }
);

// 存储从接口获取的所有产品数据
const allProducts = ref<ProductItem[]>([]);

// 按主题分组的产品数据
const productTopics = ref<ProductTopic[]>([]);

// 滚动容器引用映射
const scrollRefs = ref<Record<string | number, HTMLElement | undefined>>({});

// 跳转到产品详情页（在当前页面打开）
const goToProductDetail = (product: ProductItem, category: string) => {
  // 使用Vue Router在当前页面导航到产品详情页
  router.push({
    path: '/product-detail',
    query: {
      category: category,
      product: JSON.stringify(product)
    }
  });
};

// 组件挂载时获取产品数据
onMounted(() => {
  fetchProductData();
});

// 从接口获取产品数据
const fetchProductData = async () => {
  try {
    const response = await fetch('/jeecg-boot/energycube/topicImg/queryAllTopicImg');
    const data = await response.json();
    if (data.code === 200 && data.result) {
      // 保存原始产品数据
      allProducts.value = data.result;
      // 按主题分组并排序
      groupProductsByTopic();
    }
    console.log("产品数据：", allProducts.value);
  } catch (error) {
    console.error('获取产品数据失败:', error);
    // 如果接口调用失败，可以使用模拟数据
    useMockData();
  }
};

// 按主题分组产品数据
const groupProductsByTopic = () => {
  // 按主题ID分组
  const topicMap = new Map<number, ProductTopic>();
  
  allProducts.value.forEach(product => {
    const topic = product.topicVO;
    if (!topicMap.has(topic.id)) {
      topicMap.set(topic.id, {
        id: topic.id,
        name: topic.name,
        nameEn: topic.nameEn,
        sort: topic.sort,
        products: []
      });
    }
    
    // 添加产品到对应主题
    topicMap.get(topic.id)?.products.push(product);
  });
  
  // 对产品排序
  topicMap.forEach(topic => {
    topic.products.sort((a, b) => a.sort - b.sort);
  });
  
  // 将Map转换为数组并按主题排序
  productTopics.value = Array.from(topicMap.values()).sort((a, b) => a.sort - b.sort);
};

// 使用模拟数据（接口调用失败时使用）
const useMockData = () => {
  const mockData: ProductItem[] = [
    {
      id: 1,
      imgUrl: 'https://picsum.photos/id/1/600/400',
      imgUrlen: 'https://picsum.photos/id/1/600/400',
      sort: 1,
      productName: 'EVlink Cross(連接線)',
      productNameEn: 'EVlink Cross(Connector)',
      productPrice: 2098,
      productDesc: '家用/公共充電站通用充電線',
      productDescEn: 'Universal charging cable for home/public charging stations',
      isNew: true,
      topicVO: {
        id: 1,
        name: '充電線',
        nameEn: 'Charging cable',
        sort: 1
      }
    },
    {
      id: 2,
      imgUrl: 'https://picsum.photos/id/2/600/400',
      imgUrlen: 'https://picsum.photos/id/2/600/400',
      sort: 2,
      productName: 'NTT DC車用充電線',
      productNameEn: 'NTT DC Vehicle Charging Cable',
      productPrice: 1898,
      productDesc: '車用充電連接線',
      productDescEn: 'Vehicle charging cable',
      isNew: false,
      topicVO: {
        id: 1,
        name: '充電線',
        nameEn: 'Charging cable',
        sort: 1
      }
    },
    {
      id: 3,
      imgUrl: 'https://picsum.photos/id/5/600/400',
      imgUrlen: 'https://picsum.photos/id/5/600/400',
      sort: 1,
      productName: 'EVlink Portable(便攜式充電器)',
      productNameEn: 'EVlink Portable(Portable Charger)',
      productPrice: 3680,
      productDesc: '家用/公共充電站通用充電線',
      productDescEn: 'Universal charging cable for home/public charging stations',
      isNew: true,
      isHot: false,
      topicVO: {
        id: 2,
        name: '便攜式充電器',
        nameEn: 'Portable charger',
        sort: 2
      }
    },
    {
      id: 4,
      imgUrl: 'https://picsum.photos/id/6/600/400',
      imgUrlen: 'https://picsum.photos/id/6/600/400',
      sort: 2,
      productName: 'EVCAR.EU EASY(便攜式充電器)',
      productNameEn: 'EVCAR.EU EASY(Portable Charger)',
      productPrice: 3480,
      productDesc: '家用/公共充電站通用充電線',
      productDescEn: 'Universal charging cable for home/public charging stations',
      isNew: false,
      isHot: true,
      topicVO: {
        id: 2,
        name: '便攜式充電器',
        nameEn: 'Portable charger',
        sort: 2
      }
    }
  ];
  
  allProducts.value = mockData;
  groupProductsByTopic();
};

// 兼容车型品牌数据
const carBrands = ref([
  { name: 'Audi', logoUrl: 'https://rock-printer.oss-cn-shenzhen.aliyuncs.com/brand_logo/%E5%A5%A5%E8%BF%AA.png' },
  { name: 'Mercedes-Benz', logoUrl: 'https://rock-printer.oss-cn-shenzhen.aliyuncs.com/brand_logo/%E5%A5%94%E9%A9%B0.png' },
  { name: 'BMW', logoUrl: 'https://rock-printer.oss-cn-shenzhen.aliyuncs.com/brand_logo/%E5%AE%9D%E9%A9%AC%20%281%29.png' },
  { name: 'BYD', logoUrl: 'https://rock-printer.oss-cn-shenzhen.aliyuncs.com/brand_logo/%E6%AF%94%E4%BA%9A%E8%BF%AA%20%281%29.png' },
  { name: 'Ford', logoUrl: 'https://rock-printer.oss-cn-shenzhen.aliyuncs.com/brand_logo/%E7%A6%8F%E7%89%B9%20%281%29.png' },
  { name: 'Honda', logoUrl: 'https://rock-printer.oss-cn-shenzhen.aliyuncs.com/brand_logo/%E6%9C%AC%E7%94%B0.png' },
  { name: 'Hyundai', logoUrl: 'https://rock-printer.oss-cn-shenzhen.aliyuncs.com/brand_logo/icon_lg_hyundai.png' },
  { name: 'Nissan', logoUrl: 'https://rock-printer.oss-cn-shenzhen.aliyuncs.com/brand_logo/%E6%97%A5%E4%BA%A7.png' },
  { name: 'Lexus', logoUrl: 'https://rock-printer.oss-cn-shenzhen.aliyuncs.com/brand_logo/%E9%9B%B7%E5%85%8B%E8%90%A8%E6%96%AF.png' },
  { name: 'Porsche', logoUrl: 'https://rock-printer.oss-cn-shenzhen.aliyuncs.com/brand_logo/porsche%20%281%29.png' },
  { name: 'Volvo', logoUrl: 'https://rock-printer.oss-cn-shenzhen.aliyuncs.com/brand_logo/volvo%20%281%29.png' },
  { name: 'Volkswagen', logoUrl: 'https://rock-printer.oss-cn-shenzhen.aliyuncs.com/brand_logo/volkswagen.png' },
  { name: 'Kia', logoUrl: 'https://rock-printer.oss-cn-shenzhen.aliyuncs.com/brand_logo/kia.png' },
  { name: 'Maserati', logoUrl: 'https://rock-printer.oss-cn-shenzhen.aliyuncs.com/brand_logo/maserati.png' },
  { name: '吉利', logoUrl: 'https://rock-printer.oss-cn-shenzhen.aliyuncs.com/brand_logo/%E5%90%89%E5%88%A9%E9%93%B6%E6%B2%B3.png' },
  { name: 'Tesla', logoUrl: 'https://rock-printer.oss-cn-shenzhen.aliyuncs.com/brand_logo/Tesla.png' },
  { name: 'Toyota', logoUrl: 'https://rock-printer.oss-cn-shenzhen.aliyuncs.com/brand_logo/toyota.png' },
  { name: '奇瑞', logoUrl: 'https://rock-printer.oss-cn-shenzhen.aliyuncs.com/brand_logo/%E5%A5%87%E7%91%9E.png' }
]);

// 水平滚动函数
function scrollHorizontally(topicId: number | string, direction: number) {
  const scrollRef = scrollRefs.value[topicId];
  const scrollAmount = 300; // 每次滚动的距离

  // 执行滚动
  if (scrollRef) {
    scrollRef.scrollBy({
      left: scrollAmount * direction,
      behavior: 'smooth'
    });
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(productTopics.value, (topic) => {
      return (_openBlock(), _createElementBlock("section", {
        key: topic.id,
        class: "product-section"
      }, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("h2", _hoisted_3, _toDisplayString(currentLanguage.value === 'zh-Hant' ? topic.name : topic.nameEn), 1),
          (topic.products.length > 4)
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                _createElementVNode("button", {
                  class: "scroll-btn scroll-left",
                  onClick: ($event: any) => (scrollHorizontally(topic.id, -1)),
                  "aria-label": "向左滚动"
                }, " < ", 8, _hoisted_5),
                _createElementVNode("button", {
                  class: "scroll-btn scroll-right",
                  onClick: ($event: any) => (scrollHorizontally(topic.id, 1)),
                  "aria-label": "向右滚动"
                }, " > ", 8, _hoisted_6)
              ]))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_7, [
          _createElementVNode("div", {
            class: "product-scroll-wrapper",
            ref_for: true,
            ref: el => { scrollRefs.value[topic.id] = el }
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(topic.products, (product) => {
              return (_openBlock(), _createElementBlock("div", {
                key: product.id,
                class: "product-item",
                onClick: ($event: any) => (goToProductDetail(product, currentLanguage.value === 'zh-Hant' ? topic.name : topic.nameEn))
              }, [
                _createElementVNode("div", _hoisted_9, [
                  _createElementVNode("img", {
                    src: currentLanguage.value === 'zh-Hant' ? product.imgUrl : product.imgUrlen,
                    alt: currentLanguage.value === 'zh-Hant' ? product.productName : product.productNameEn,
                    class: "product-image"
                  }, null, 8, _hoisted_10),
                  (product.isNew)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_11, "新品"))
                    : _createCommentVNode("", true),
                  (product.isHot)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_12, "热销"))
                    : _createCommentVNode("", true)
                ]),
                _createElementVNode("h3", _hoisted_13, _toDisplayString(currentLanguage.value === 'zh-Hant' ? product.productName : product.productNameEn), 1),
                _createElementVNode("p", _hoisted_14, _toDisplayString(currentLanguage.value === 'zh-Hant' ? product.productDesc : product.productDescEn), 1),
                _createElementVNode("p", _hoisted_15, "HK$" + _toDisplayString(product.productPrice.toFixed(2)), 1)
              ], 8, _hoisted_8))
            }), 128))
          ], 512)
        ])
      ]))
    }), 128)),
    _createElementVNode("section", _hoisted_16, [
      _createElementVNode("h2", _hoisted_17, _toDisplayString(currentLanguage.value === 'zh-Hant' ? '機乎可與市面上所有電動車相容' : 'Compatible with almost all electric vehicles on the market'), 1),
      _createElementVNode("div", _hoisted_18, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(carBrands.value, (brand, index) => {
          return (_openBlock(), _createElementBlock("div", {
            key: index,
            class: "car-brand-item"
          }, [
            _createElementVNode("img", {
              src: brand.logoUrl,
              alt: brand.name,
              class: "car-brand-logo"
            }, null, 8, _hoisted_19)
          ]))
        }), 128))
      ])
    ])
  ]))
}
}

})